import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCachedResource } from "../hooks/useCachedResource";
import { eosAccrue, eosPreview } from "../api/acctIfrs";
import { acctSettings, saveAcctSettings } from "../api/accounting";
import { AcctShell } from "../features/acct/AcctShell";
import { NeuButton, NeuCard, NeuInput } from "../components/neu/Neu";
import { useApiError } from "../hooks/useApiError";

export default function AcctEosPage() {
  const { t } = useTranslation();
  const describe = useApiError();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const { data, reload: load } = useCachedResource(`acct-eos-${month}`, () => eosPreview(month), [month]);
  const [months, setMonths] = useState("1");

  useEffect(() => {
    acctSettings().then((s) => setMonths(s.eos_months_per_year));
  }, []);

  const saveRule = async () => {
    await saveAcctSettings({ eos_months_per_year: months });
    toast.success(t("acct.settingsSaved"));
    load();
  };

  const accrue = async () => {
    try {
      const res = await eosAccrue(month);
      toast.success(res.posted ? `${t("acct.ifrs.eosPosted")} ${res.entry_number}` : t(`acct.errors.${res.reason}`));
      load();
    } catch (error) {
      toast.error(describe(error));
    }
  };

  return (
    <AcctShell titleKey="eos">
      <NeuCard className="flex flex-wrap items-end gap-4 p-5" data-testid="eos-rule">
        <div className="w-40">
          <p className="label-xs mb-2">{t("acct.ifrs.monthsPerYear")}</p>
          <NeuInput type="number" step="0.25" data-testid="eos-months" value={months} onChange={(e) => setMonths(e.target.value)} />
        </div>
        <NeuButton data-testid="eos-rule-save" onClick={saveRule}>{t("app.save")}</NeuButton>
        <p className="text-[11px] text-muted-foreground">{t("acct.ifrs.eosHint")}</p>
      </NeuCard>

      <NeuCard className="flex flex-wrap items-end gap-4 p-5" data-testid="eos-box">
        <div className="w-44">
          <p className="label-xs mb-2">{t("acct.payrollPost.month")}</p>
          <NeuInput type="month" data-testid="eos-month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <NeuButton variant="primary" data-testid="eos-accrue" disabled={data?.posted} onClick={accrue}>
          {data?.posted ? t("acct.ifrs.eosAlready") : t("acct.ifrs.eosAccrue")}
        </NeuButton>
        <div className="grid gap-1">
          <span className="text-[11px] text-muted-foreground">{t("acct.ifrs.eosTotal")}</span>
          <span className="font-mono text-lg font-black" dir="ltr" data-testid="eos-total">{data?.total ?? "0"}</span>
        </div>
        <span className="font-mono text-[11px] text-primary" dir="ltr" data-testid="eos-entry">{data?.entry_number || ""}</span>
      </NeuCard>

      <NeuCard className="p-5" data-testid="eos-rows">
        <p className="label-xs mb-3">{t("acct.ifrs.eosEmployees")}</p>
        <div className="grid gap-2">
          {(data?.rows || []).map((row) => (
            <div key={row.user_id} className="flex items-center gap-3 rounded-xl px-3 py-2 shadow-neu-in">
              <span className="min-w-0 flex-1 truncate text-xs font-semibold">{row.name}</span>
              <span className="font-mono text-xs font-bold" dir="ltr">{row.accrual}</span>
            </div>
          ))}
        </div>
      </NeuCard>
    </AcctShell>
  );
}
